import React, { useState, useEffect } from "react";
import api from "../../../services/api";

const COMMON_ACTIVE_INGREDIENTS = [
  "Semaglutida",
  "Tirzepatida",
  "Liraglutida",
  "Dulaglutida",
  "Metformina",
  "Bupropiona",
  "Naltrexona",
  "Topiramato",
  "Creatina",
  "Whey Protein",
  "Colágeno",
  "Glutamina",
  "Sibutramina",
  "Lisdexanfetamina",
];

// Helper para sanitizar entrada numérica (permite apenas dígitos e vírgula/ponto decimal)
const sanitizeNumeric = (val) => {
  if (val === null || val === undefined) return "";
  let cleaned = String(val).replace(/[^0-9.,]/g, "");
  const parts = cleaned.split(/[.,]/);
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }
  return cleaned;
};

export default function SimplifiedMedicationModal({
  isOpen,
  onClose,
  onSuccess,
  initialActiveIngredient = "",
  editingItem = null,
  templateItem = null,
}) {
  const [activeIngredient, setActiveIngredient] = useState(initialActiveIngredient);
  const [brandLabel, setBrandLabel] = useState("");

  // 4 Apresentações amigáveis: FRASCO, CANETA, COMPRIMIDO, PO
  const [presentation, setPresentation] = useState("FRASCO");

  // COMPRIMIDO / CÁPSULA
  const [tabletDose, setTabletDose] = useState("");
  const [tabletUnit, setTabletUnit] = useState("mg"); // mcg, mg, g
  const [tabletCount, setTabletCount] = useState("");

  // INJETÁVEL / CANETA
  const [penDoseMg, setPenDoseMg] = useState("");
  const [penCount, setPenCount] = useState("1");

  // INJETÁVEL / FRASCO
  const [vialLabelMg, setVialLabelMg] = useState("");
  const [vialLabelMl, setVialLabelMl] = useState("0.5");
  const [vialLiquidMl, setVialLiquidMl] = useState("2");
  const [vialCount, setVialCount] = useState("1");

  // SUPLEMENTO EM PÓ
  const [scoopGrams, setScoopGrams] = useState("");
  const [totalPackageGrams, setTotalPackageGrams] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isExistingIngredient = Boolean(editingItem || templateItem || initialActiveIngredient);

  useEffect(() => {
    const item = editingItem || templateItem;
    const activeName = (
      editingItem?.medicationName ||
      editingItem?.name ||
      templateItem?.medicationName ||
      templateItem?.name ||
      initialActiveIngredient ||
      ""
    ).trim();

    if (activeName) {
      setActiveIngredient(activeName);
    }

    const nameLower = activeName.toLowerCase();
    const target = item?.packagingItems?.[0] || item;

    // Determinar o tipo da medicação/suplemento
    let type = String(
      target?.medicationType ||
      templateItem?.medicationType ||
      ""
    ).toUpperCase();

    if (
      !type ||
      type === "UNDEFINED" ||
      type === "COMPRIMIDO" ||
      type === "OUTRO"
    ) {
      if (
        nameLower.includes("glutamina") ||
        nameLower.includes("creatina") ||
        nameLower.includes("whey") ||
        nameLower.includes("colageno") ||
        nameLower.includes("colágeno") ||
        nameLower.includes("proteina") ||
        nameLower.includes("proteína") ||
        target?.unitLabel === "doses" ||
        templateItem?.medicationType === "PO"
      ) {
        type = "PO";
      }
    }

    if (item || activeName) {
      if (target?.packagingLabel || target?.brand) {
        setBrandLabel(target.packagingLabel || target.brand);
      }

      if (type === "INJETAVEL") {
        const form = String(target?.medicationForm || "").toUpperCase();
        if (
          form.includes("CANETA") ||
          String(target?.packagingKind || "").toUpperCase().includes("CANETA")
        ) {
          setPresentation("CANETA");
          setPenDoseMg(String(target?.mgBase || target?.mgPerMl || ""));
          setPenCount(String(target?.totalUnits || "1"));
        } else {
          setPresentation("FRASCO");
          setVialLabelMg(String(target?.mgBase || target?.mgPerMl || ""));
          setVialLabelMl(String(target?.mlBase || "0.5"));
          setVialLiquidMl(String(target?.totalVolumeMl || "2"));
          setVialCount("1");
        }
      } else if (type === "PO" || type === "PÓ") {
        setPresentation("PO");
        const mgPerUnit = Number(target?.mgPerUnit || target?.mg_per_unit || target?.mgBase || 3000);
        const gPerScoop = mgPerUnit > 100 ? mgPerUnit / 1000 : mgPerUnit;
        const totScoops = Number(target?.totalUnits || target?.total_units || 30) || 30;

        setScoopGrams(String(gPerScoop || 3));
        setTotalPackageGrams(String(gPerScoop * totScoops || 300));
      } else if (type === "COMPRIMIDO" || type === "CAPSULA") {
        setPresentation("COMPRIMIDO");
        const rawMg = Number(target?.mgPerUnit || target?.mgBase || 0);
        const unit = (target?.unitLabel || "mg").toLowerCase();

        if (unit === "mcg") {
          setTabletDose(String(rawMg * 1000));
          setTabletUnit("mcg");
        } else if (unit === "g") {
          setTabletDose(String(rawMg / 1000));
          setTabletUnit("g");
        } else {
          setTabletDose(String(rawMg || ""));
          setTabletUnit("mg");
        }

        setTabletCount(String(target?.totalUnits || target?.remainingUnits || "30"));
      }
    }
  }, [editingItem, templateItem, initialActiveIngredient]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError("");

    const cleanActiveName = activeIngredient.trim();
    if (!cleanActiveName) {
      setError("Informe o Princípio Ativo / Nome do medicamento ou suplemento.");
      return;
    }

    let payload = {};

    if (presentation === "COMPRIMIDO") {
      const doseNum = Number(String(tabletDose).replace(",", "."));
      if (!Number.isFinite(doseNum) || doseNum <= 0) {
        setError("Informe a dosagem por comprimido/cápsula (somente números positivos).");
        return;
      }

      // Converter dosagem conforme unidade (mcg, mg, g) para base em mg
      let mgVal = doseNum;
      if (tabletUnit === "mcg") {
        mgVal = doseNum / 1000;
      } else if (tabletUnit === "g") {
        mgVal = doseNum * 1000;
      }

      const countVal = Number(String(tabletCount).replace(",", "."));
      if (!Number.isFinite(countVal) || countVal <= 0) {
        setError("Informe a quantidade total de comprimidos/cápsulas na caixa.");
        return;
      }

      payload = {
        name: cleanActiveName,
        medicationName: cleanActiveName,
        brand: brandLabel.trim() || undefined,
        packagingLabel: brandLabel.trim() || undefined,
        medicationType: "COMPRIMIDO",
        medicationForm: "COMPRIMIDO_UNIDADE",
        unitLabel: tabletUnit,

        mgPerUnit: mgVal,
        totalUnits: countVal,
        remainingUnits: countVal,
        mgBase: mgVal,
        mlBase: 1,
        mgPerMl: mgVal,
        totalVolumeMl: countVal,

        status: editingItem?.status || "DISPONIVEL",
      };
    } else if (presentation === "CANETA") {
      const mgVal = Number(String(penDoseMg).replace(",", "."));
      if (!Number.isFinite(mgVal) || mgVal <= 0) {
        setError("Informe a dosagem/concentração em mg (somente números positivos).");
        return;
      }

      const countVal = Number(String(penCount).replace(",", ".")) || 1;

      payload = {
        name: cleanActiveName,
        medicationName: cleanActiveName,
        brand: brandLabel.trim() || undefined,
        packagingLabel: brandLabel.trim() || undefined,
        medicationType: "INJETAVEL",
        medicationForm: "INJETAVEL_MG_ML",

        mgBase: mgVal,
        mlBase: 1,
        mgPerMl: mgVal,
        totalVolumeMl: countVal * 3,
        totalMgAvailable: mgVal * countVal * 10,
        remainingMg: mgVal * countVal * 10,

        status: editingItem?.status || "DISPONIVEL",
      };
    } else if (presentation === "FRASCO") {
      const labelMg = Number(String(vialLabelMg).replace(",", "."));
      if (!Number.isFinite(labelMg) || labelMg <= 0) {
        setError("Informe a massa escrita no rótulo em mg (somente números positivos).");
        return;
      }

      const labelMl = Number(String(vialLabelMl).replace(",", ".")) || 1;
      const liquidMl = Number(String(vialLiquidMl).replace(",", ".")) || labelMl;
      const countVal = Number(String(vialCount).replace(",", ".")) || 1;

      const mgPerMlVal = labelMg / labelMl;
      const totalMlInBox = liquidMl * countVal;
      const totalMgInBox = mgPerMlVal * totalMlInBox;

      payload = {
        name: cleanActiveName,
        medicationName: cleanActiveName,
        brand: brandLabel.trim() || undefined,
        packagingLabel: brandLabel.trim() || undefined,
        medicationType: "INJETAVEL",
        medicationForm: "INJETAVEL_MG_ML",

        mgBase: labelMg,
        mlBase: labelMl,
        mgPerMl: mgPerMlVal,
        totalVolumeMl: totalMlInBox,
        totalMgAvailable: totalMgInBox,
        remainingMg: totalMgInBox,

        status: editingItem?.status || "DISPONIVEL",
      };
    } else if (presentation === "PO") {
      const scoopVal = Number(String(scoopGrams).replace(",", "."));
      if (!Number.isFinite(scoopVal) || scoopVal <= 0) {
        setError("Informe os gramas por doseador/scoop (somente números positivos).");
        return;
      }

      const totalVal = Number(String(totalPackageGrams).replace(",", "."));
      if (!Number.isFinite(totalVal) || totalVal <= 0) {
        setError("Informe o peso total do pote/embalagem em gramas (somente números positivos).");
        return;
      }

      const totalScoops = Math.floor(totalVal / scoopVal) || 1;
      const mgPerScoop = scoopVal * 1000;

      payload = {
        name: cleanActiveName,
        medicationName: cleanActiveName,
        brand: brandLabel.trim() || undefined,
        packagingLabel: brandLabel.trim() || undefined,
        medicationType: "PO",
        medicationForm: "PO_DOSES",
        unitLabel: "doses",

        mgPerUnit: mgPerScoop,
        totalUnits: totalScoops,
        remainingUnits: totalScoops,
        mgBase: mgPerScoop,
        mlBase: 1,
        mgPerMl: mgPerScoop,
        totalVolumeMl: totalScoops,
        totalMgAvailable: mgPerScoop * totalScoops,
        remainingMg: mgPerScoop * totalScoops,

        status: editingItem?.status || "DISPONIVEL",
      };
    }

    try {
      setLoading(true);
      if (editingItem?.id) {
        await api.put(`/flasks/${editingItem.id}`, payload);
      } else {
        await api.post("/flasks", payload);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message;
      setError(msg || "Não foi possível salvar a embalagem no momento.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-[#1A1024]/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="liti-card w-full max-w-lg rounded-[28px] p-6 shadow-2xl border border-[#ECE7FA] bg-white space-y-4 max-h-[85vh] overflow-y-auto my-auto">
        {/* CABEÇALHO */}
        <div className="flex items-center justify-between border-b border-[#ECE7FA] pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#6C54D8] text-white font-black text-xl shadow-xs">
              {editingItem ? "✏️" : "📦"}
            </div>
            <div>
              <h3 className="text-base font-bold text-[#24152F] font-serif">
                {editingItem
                  ? "Editar Embalagem"
                  : isExistingIngredient
                    ? `Adicionar Caixa a ${activeIngredient}`
                    : "Cadastrar Embalagem no Estoque"}
              </h3>
              <p className="text-xs font-medium text-[#6D6680]">
                {isExistingIngredient && !editingItem
                  ? "Adicionando caixa com configurações pré-carregadas da medicação"
                  : "Copie os valores diretamente da caixa do medicamento"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-[#6C54D8] font-bold text-lg p-1 cursor-pointer"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-xs font-semibold text-red-800 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 1. PRINCÍPIO ATIVO */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680]">
                Princípio Ativo / Nome do Medicamento ou Suplemento
              </label>

              {isExistingIngredient && (
                <span className="text-[10px] font-bold text-[#6C54D8] bg-[#ECE7FA] px-2 py-0.5 rounded-full flex items-center gap-1">
                  🔒 Vínculo Fixo
                </span>
              )}
            </div>

            <input
              type="text"
              list={isExistingIngredient ? undefined : "common-ingredients"}
              value={activeIngredient}
              onChange={(e) => setActiveIngredient(e.target.value)}
              readOnly={isExistingIngredient}
              placeholder="Ex: Semaglutida, Tirzepatida, Creatina, Whey Protein..."
              required
              className={`input-base w-full h-11 text-xs font-bold ${
                isExistingIngredient
                  ? "bg-[#ECE7FA]/60 text-[#6C54D8] cursor-not-allowed border-[#ECE7FA]"
                  : ""
              }`}
            />
            {!isExistingIngredient && (
              <datalist id="common-ingredients">
                {COMMON_ACTIVE_INGREDIENTS.map((item) => (
                  <option key={item} value={item} />
                ))}
              </datalist>
            )}
          </div>

          {/* 2. SELEÇÃO DAS 4 APRESENTAÇÕES AMIGÁVEIS */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1.5">
              Como vem o produto no rótulo?
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setPresentation("FRASCO")}
                className={`py-2.5 px-3 rounded-2xl text-xs font-bold transition-all border flex items-center justify-center gap-1.5 cursor-pointer ${
                  presentation === "FRASCO"
                    ? "bg-[#6C54D8] text-white border-[#6C54D8] shadow-xs"
                    : "bg-white text-[#24152F] border-[#ECE7FA] hover:bg-[#ECE7FA]/60"
                }`}
              >
                🧪 Injetável / Frasco
              </button>

              <button
                type="button"
                onClick={() => setPresentation("CANETA")}
                className={`py-2.5 px-3 rounded-2xl text-xs font-bold transition-all border flex items-center justify-center gap-1.5 cursor-pointer ${
                  presentation === "CANETA"
                    ? "bg-[#6C54D8] text-white border-[#6C54D8] shadow-xs"
                    : "bg-white text-[#24152F] border-[#ECE7FA] hover:bg-[#ECE7FA]/60"
                }`}
              >
                💉 Injetável / Caneta
              </button>

              <button
                type="button"
                onClick={() => setPresentation("COMPRIMIDO")}
                className={`py-2.5 px-3 rounded-2xl text-xs font-bold transition-all border flex items-center justify-center gap-1.5 cursor-pointer ${
                  presentation === "COMPRIMIDO"
                    ? "bg-[#6C54D8] text-white border-[#6C54D8] shadow-xs"
                    : "bg-white text-[#24152F] border-[#ECE7FA] hover:bg-[#ECE7FA]/60"
                }`}
              >
                💊 Comprimido / Cápsula
              </button>

              <button
                type="button"
                onClick={() => setPresentation("PO")}
                className={`py-2.5 px-3 rounded-2xl text-xs font-bold transition-all border flex items-center justify-center gap-1.5 cursor-pointer ${
                  presentation === "PO"
                    ? "bg-[#6C54D8] text-white border-[#6C54D8] shadow-xs"
                    : "bg-white text-[#24152F] border-[#ECE7FA] hover:bg-[#ECE7FA]/60"
                }`}
              >
                🥤 Suplemento em Pó
              </button>
            </div>
          </div>

          {/* 3. FORMULÁRIOS ESPECÍFICOS */}
          {presentation === "FRASCO" && (
            <div className="space-y-3.5 p-4 rounded-2xl bg-[#ECE7FA]/50 border border-[#ECE7FA]">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#6C54D8] block">
                  1. O que está escrito no Rótulo da Caixa?
                </span>
                <p className="text-[10px] text-[#6D6680] font-medium">
                  Copie os valores exatos (ex: <code className="font-bold text-[#24152F]">15 mg / 0,5 mL</code>)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-[#24152F] mb-1">
                    Massa (mg)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={vialLabelMg}
                    onChange={(e) => setVialLabelMg(sanitizeNumeric(e.target.value))}
                    placeholder="Ex: 15"
                    required
                    className="input-base w-full h-10 text-xs font-medium"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-[#24152F] mb-1">
                    em Volume (mL)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={vialLabelMl}
                    onChange={(e) => setVialLabelMl(sanitizeNumeric(e.target.value))}
                    placeholder="Ex: 0.5 ou 1"
                    required
                    className="input-base w-full h-10 text-xs font-medium"
                  />
                </div>
              </div>

              <div className="pt-2 border-t border-[#ECE7FA]">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#6C54D8] block mb-1">
                  2. O que vem dentro da caixa?
                </span>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-[#24152F] mb-1">
                      Volume por Frasco (mL)
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={vialLiquidMl}
                      onChange={(e) => setVialLiquidMl(sanitizeNumeric(e.target.value))}
                      placeholder="Ex: 2 ou 0.5"
                      required
                      className="input-base w-full h-10 text-xs font-medium"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-[#24152F] mb-1">
                      Qtd de Frascos
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={vialCount}
                      onChange={(e) => setVialCount(sanitizeNumeric(e.target.value))}
                      placeholder="Ex: 1 ou 4"
                      required
                      className="input-base w-full h-10 text-xs font-medium"
                    />
                  </div>
                </div>
              </div>

              {vialLabelMg && vialLabelMl && Number(vialLabelMl) > 0 && (
                <div className="text-[11px] font-bold text-[#6C54D8] bg-white p-2.5 rounded-xl border border-[#ECE7FA] text-center shadow-xs">
                  ✨ O MariaM calculou: Concentração de <span className="text-[#24152F] font-black">{(Number(vialLabelMg) / Number(vialLabelMl)).toFixed(1)} mg/mL</span>
                  {" — "}Total no estoque: <span className="text-[#24152F] font-black">{((Number(vialLabelMg) / Number(vialLabelMl)) * Number(vialLiquidMl || 1) * Number(vialCount || 1)).toFixed(1)} mg</span>
                </div>
              )}
            </div>
          )}

          {presentation === "CANETA" && (
            <div className="space-y-3 p-3.5 rounded-2xl bg-[#ECE7FA]/50 border border-[#ECE7FA]">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#6C54D8] block">
                💉 Caneta de Aplicação (Ex: Ozempic, Saxenda, Victoza, Mounjaro)
              </span>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-[#24152F] mb-1">
                    Dose no Rótulo (mg)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={penDoseMg}
                    onChange={(e) => setPenDoseMg(sanitizeNumeric(e.target.value))}
                    placeholder="Ex: 0.5, 1, 2.5"
                    required
                    className="input-base w-full h-10 text-xs font-medium"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-[#24152F] mb-1">
                    Qtd de Canetas na Caixa
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={penCount}
                    onChange={(e) => setPenCount(sanitizeNumeric(e.target.value))}
                    placeholder="Ex: 1"
                    required
                    className="input-base w-full h-10 text-xs font-medium"
                  />
                </div>
              </div>
            </div>
          )}

          {presentation === "COMPRIMIDO" && (
            <div className="grid grid-cols-2 gap-3 p-3.5 rounded-2xl bg-[#ECE7FA]/50 border border-[#ECE7FA]">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-[#6C54D8]">
                    Dose por Unidade
                  </label>

                  {/* UNIDADE: MCG, MG, G */}
                  <select
                    value={tabletUnit}
                    onChange={(e) => setTabletUnit(e.target.value)}
                    className="text-[11px] font-bold text-[#6C54D8] bg-white border border-[#ECE7FA] rounded-lg px-1.5 py-0.5 outline-none cursor-pointer"
                  >
                    <option value="mcg">mcg</option>
                    <option value="mg">mg</option>
                    <option value="g">g</option>
                  </select>
                </div>

                <input
                  type="text"
                  inputMode="decimal"
                  value={tabletDose}
                  onChange={(e) => setTabletDose(sanitizeNumeric(e.target.value))}
                  placeholder={`Ex: 30 ${tabletUnit}`}
                  required
                  className="input-base w-full h-10 text-xs font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#6C54D8] mb-1">
                  Qtd na Caixa
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={tabletCount}
                  onChange={(e) => setTabletCount(sanitizeNumeric(e.target.value))}
                  placeholder="Ex: 30 comprimidos"
                  required
                  className="input-base w-full h-10 text-xs font-medium"
                />
              </div>
            </div>
          )}

          {presentation === "PO" && (
            <div className="grid grid-cols-2 gap-3 p-3.5 rounded-2xl bg-[#ECE7FA]/50 border border-[#ECE7FA]">
              <div>
                <label className="block text-xs font-bold text-[#6C54D8] mb-1">
                  Gramas por Scoop (g)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={scoopGrams}
                  onChange={(e) => setScoopGrams(sanitizeNumeric(e.target.value))}
                  placeholder="Ex: 3g ou 30g"
                  required
                  className="input-base w-full h-10 text-xs font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#6C54D8] mb-1">
                  Peso Total do Pote (g)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={totalPackageGrams}
                  onChange={(e) => setTotalPackageGrams(sanitizeNumeric(e.target.value))}
                  placeholder="Ex: 300g ou 900g"
                  required
                  className="input-base w-full h-10 text-xs font-medium"
                />
              </div>
            </div>
          )}

          {/* 4. MARCA / RÓTULO */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1">
              Marca / Nome Comercial <span className="text-gray-400 font-normal">(Opcional)</span>
            </label>
            <input
              type="text"
              value={brandLabel}
              onChange={(e) => setBrandLabel(e.target.value)}
              placeholder="Ex: Ozempic, Wegovy, Mounjaro, Creatina Creapure..."
              className="input-base w-full h-10 text-xs font-medium"
            />
          </div>

          {/* BOTÕES DE AÇÃO */}
          <div className="flex gap-2 pt-3 border-t border-[#ECE7FA]">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn-secondary flex-1 h-11 text-xs font-bold cursor-pointer"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1 h-11 text-xs font-bold uppercase tracking-wider shadow-md flex items-center justify-center gap-2 cursor-pointer"
            >
              {loading ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Salvando...
                </>
              ) : (
                "Salvar no Estoque"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
